<script setup>
definePage({
  meta: {
    action: 'read',
    subject: 'subject',
    navActiveLink: 'subjects',
  },
})

import { samples, sex, species } from '@/datas/subject'
import { getProjectId } from "@/utils/cdms"
import { isLockedProject } from '@/utils/project'
import { useLoading } from '@/utils/stores/loading'
import { useSnackBar } from '@/utils/stores/snackbar'

const router = useRouter()
const loadingStore = useLoading()
const snackbarStore = useSnackBar()

const data = reactive({
  projectId: getProjectId(),
  siteId: "",
  consentDate: "",
  subjectNo: "S" + "00" + "000",
  name: "",
  type: 0,
  sample: [],
  chartNo: "",
  species: "",
  breed: "",
  birthday: new Date().toISOString().slice(0, 10),
  sex: "",
})

if(await isLockedProject()) {router.back()}

const refForm = ref(null)

const years = Array.from({ length: 30 }, (_, i) => i)
const months = Array.from({ length: 12 }, (_, i) => i)
const siteSelects = ref([])
const speciesSelects = species
const sexSelects = sex

const breedSelects = computed(() => {
  return speciesSelects.find(species => {return species.value == data.species}) ?? []
})

watch(() => data.species, () => {
  data.breed = ""
})

const {
  data: siteData,
  execute: fetchSites,
} = await useApi(createUrl('/sources/sites/all', {
  query: {
    projectId: getProjectId(),
    type: 'investigator',
  },
}))

siteSelects.value = siteData.value
if(siteSelects.value.length == 1) data.siteId =  siteSelects.value[0].id

const lastSubjectData = ref()

const fetchSubject = async () => {
  const {
    data: result,
    execute: fetchSubject1,
  } = await useApi(createUrl('/subjects/last', {
    query: {
      projectId: getProjectId(),
      siteId: data.siteId,
    },
  }))

  lastSubjectData.value = result.value
}

const getSubjectNo = async () => {
  let site = siteSelects.value.find(s => s.id == data.siteId)
  if(!site) data.subjectNo = "S" + "00" + "000"

  await fetchSubject()
  let subjectId = lastSubjectData.value ?? 0
  data.subjectNo = "S" + String(site?.site_no ?? "0").padStart(2, "0") + String(subjectId+1).padStart(3, "0")
}

watch(()=>data.siteId, async () => {
  getSubjectNo()
}, { immediate: true })

const saveSubject = async () => {
  const validate = await refForm?.value.validate()

  console.log(validate.valid)
  if(!validate.valid) return

  loadingStore.message = "저장 중..."
  loadingStore.isLoading = true

  await getSubjectNo()

  const result = await $api(`/subjects`, { method: 'POST',
    body: { subject: data } })

  loadingStore.isLoading = false

  //snackbarStore.showSnackBar("Successfully Saved!!")
  router.push({ name: `subjects-v2` })
}

const age = computed(() => {
  const today = new Date()
  const birth = new Date(data.birthday)

  let years = today.getFullYear() - birth.getFullYear()
  let months = today.getMonth() - birth.getMonth()

  if (today.getDate() < birth.getDate()) {
    months--
  }

  if (months < 0) {
    years--
    months += 12
  }

  return { years, months }
})

const ageYears = computed({
  get: () => age.value.years,
  set: newYears => {
    const today = new Date()
    const previousDate = new Date(today)

    previousDate.setFullYear(today.getFullYear() - newYears)
    previousDate.setMonth(today.getMonth() - age.value.months)

    data.birthday = previousDate.toISOString().slice(0, 10)
  },
})

const ageMonths = computed({
  get: () => age.value.months,
  set: newMonths => {
    const today = new Date()
    const previousDate = new Date(today)

    previousDate.setFullYear(today.getFullYear() - age.value.years)
    previousDate.setMonth(today.getMonth() - newMonths)

    data.birthday = previousDate.toISOString().slice(0, 10)
  },
})

const birthdayPickerConfig = computed(() => {
  return {
    maxDate: new Date(),
    dateFormat: `Y-m-d`,
  }
})

// const onChangeCombobox = (e, items) => {
//   if(!breedSelects.value.breed.includes(breedSelects.value.breed)) data.breed = ""
// }
// const breedRule = breed => breedSelects.value.breed.includes(breed) || '품종을 선택하여 주시기 바랍니다.'
</script>

<template>
  <VRow>
    <VCol
      cols="12"
      sm="12"
      md="9"
      lg="7"
      order="1"
      order-lg="1"
    >
      <VCard :title="$t('Subject')">
        <!-- <VCardTitle></VCardTitle> -->
        <VCardText class="mt-4">
          <VForm
            ref="refForm"
            @submit.prevent="() => {}"
          >
            <VRow>
              <VCol cols="12">
                <VRow no-gutters>
                  <!-- 👉 Select Site -->
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="siteId">{{ $t('Site') }}<span style="color:red">*</span></label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppSelect
                      v-if="siteSelects.length > 1"
                      v-model="data.siteId"
                      :items="siteSelects"
                      item-title="name"
                      item-value="id"
                      density="compact"
                      placeholder="Select"
                      :rules="[requiredValidator]"
                    />
                    <div v-if="siteSelects.length == 1">
                      {{ siteSelects[0].name }}
                    </div>
                    <div v-if="siteSelects.length == 0">
                      기관이 등록되어 있지 않습니다.
                    </div>
                    <!-- {{ status.find(({ value }) => value == val)?.text }} -->
                  </VCol>
                </VRow>
              </VCol>
               
              <VCol cols="12">
                <VRow no-gutters>
                  <!-- 👉 Site Subject Number -->
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="subjectNo">{{ $t('Site Subject Number') }}</label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VTextField
                      v-if="false"
                      id="subjectNo"
                      v-model="data.subjectNo"
                      persistent-placeholder
                      readonly
                      variant="plain"
                      :rules="[requiredValidator]"
                    />
                    <div>{{ data.subjectNo }}</div>
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="type">시험 대상<span style="color:red">*</span></label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    <VRadioGroup
                      v-model="data.type"
                      inline
                    >
                      <VRadio
                        label="동물"
                        :value="0"
                      />
                      <VRadio
                        label="검체"
                        :value="1"
                      />
                    </VRadioGroup>
                  </VCol>
                </VRow>
              </VCol>
              <VCol
                v-if="data.type == 1"
                cols="12"
              >
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="sample">검체종류<span style="color:red">*</span></label>
                  </VCol>
                  <VCol
                    class="demo-space-x"
                    cols="12"
                    md="9"
                  >
                    <VCheckbox
                      v-for="(sample, i) in samples"
                      :key="i"
                      v-model="data.sample"
                      :label="sample"
                      :value="sample"
                      :rules="[v => v.length > 0 && data.type == 1 || '적어도 하나 이상을 선택하세요!']"
                    />
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12">
                <VRow no-gutters>
                  <!-- 👉 Subject Name -->
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="subjectName">{{ $t('Animal Name') }}<span style="color:red">*</span></label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="subjectName"
                      v-model="data.name"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>
              <VCol cols="12">
                <VRow no-gutters>
                  <!-- 👉 Chart No -->
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label for="chartNo">{{ $t('Chart No') }}<span style="color:red">*</span></label>
                  </VCol>

                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="chartNo"
                      v-model="data.chartNo"
                      persistent-placeholder
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>
               

              <!-- 👉 submit and reset button -->
              <VCol
                offset-md="3"
                cols="12"
                md="9"
                class="d-flex gap-4"
              >
                <VBtn
                  v-if="siteSelects.length > 0"
                  type="submit"
                  @click="saveSubject"
                >
                  {{ $t('Save') }}
                </VBtn>
                <VBtn
                  color="secondary"
                  variant="tonal"
                  @click="$router.back()"
                >
                  {{ $t('Cancel') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>
